(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-fwub]';
  const NATIVE_ADD_SELECTOR =
    'button[type="submit"][name="add"], input[type="submit"][name="add"]';

  function init(root) {
    if (!root || root.dataset.fwubReady === 'true') return;
    root.dataset.fwubReady = 'true';

    const state = {
      selected: new Map(),
      mainSelected: false,
      mainBuilderQuantity: 0,
      busy: false,
      mobileStep: 1,
      skippedSteps: new Set(),
      mobileDone: false
    };

    const currency =
      root.dataset.currency ||
      window.Shopify?.currency?.active ||
      'USD';

    const routesRoot = window.Shopify?.routes?.root || '/';
    const profile = root.dataset.profile || 'freewell-builder';

    const discountMode = 'code';

    const addDestination = root.dataset.addDestination || 'cart';

    const codes = {
      12: root.dataset.code12 || 'FWBUILD12',
      15: root.dataset.code15 || 'FWBUILD15'
    };

    const mainForm = document.getElementById(root.dataset.mainFormId || '');

    const productInfo =
      root.closest('product-info') ||
      root.closest('[id^="Product-Info-"]') ||
      root.parentElement;

    const mainCard = root.querySelector(
      '[data-fwub-card][data-main-card="true"]'
    );

    const els = {
      cards: [...root.querySelectorAll('[data-fwub-card]')],
      summary: root.querySelector('[data-fwub-summary]'),
      meter: root.querySelector('[data-fwub-savings-meter]'),
      meterTitle: root.querySelector('[data-fwub-meter-title]'),
      meterValue: root.querySelector('[data-fwub-meter-value]'),
      meterTrack: root.querySelector('[data-fwub-meter-track]'),
      meterFill: root.querySelector('[data-fwub-meter-fill]'),
      meterNodes: [...root.querySelectorAll('[data-fwub-meter-track] i')],
      meterCount: root.querySelector('[data-fwub-meter-count]'),
      meterNext: root.querySelector('[data-fwub-meter-next]'),
      subtotal: root.querySelector('[data-fwub-subtotal]'),
      savingsRow: root.querySelector('[data-fwub-savings-row]'),
      savingsLabel: root.querySelector('[data-fwub-savings-label]'),
      savings: root.querySelector('[data-fwub-savings]'),
      kitSavingsRow: root.querySelector('[data-fwub-kit-savings-row]'),
      kitSavings: root.querySelector('[data-fwub-kit-savings]'),
      total: root.querySelector('[data-fwub-total]'),
      status: root.querySelector('[data-fwub-status]'),
      mobileSteps: [...root.querySelectorAll('[data-fwub-mobile-step-nav]')],
      stepPanels: [...root.querySelectorAll('[data-fwub-step-panel]')],
      stepNavSummaries: [...root.querySelectorAll('[data-fwub-mobile-step-summary]')],
      panelSummaries: [...root.querySelectorAll('[data-fwub-panel-summary]')]
    };

    const mobileQuery = window.matchMedia('(max-width: 749px)');

    function isMobileWizard() {
      return mobileQuery.matches;
    }

    function cleanSetupName(title) {
      const value = String(title || '').trim();
      if (/Mega Pro Kit|Mega/i.test(value)) return 'Mega Pro Kit';
      if (/Pro Kit/i.test(value)) return 'Pro Kit';
      if (/VND\/CPL/i.test(value)) return 'V2 VND/CPL';
      return value;
    }

    function conciseKitName(title) {
      return cleanSetupName(title);
    }

    function joinNames(names) {
      const clean = [...new Set(names.filter(Boolean))];
      if (clean.length <= 1) return clean[0] || '';
      if (clean.length === 2) return `${clean[0]} & ${clean[1]}`;
      return `${clean.slice(0, -1).join(', ')} & ${clean.at(-1)}`;
    }

    function selectedUnitsByRole(allItems, role) {
      return allItems
        .filter(item => item.role === role)
        .reduce(
          (sum, item) => sum + normalizeQuantity(item.quantity),
          0
        );
    }

    function mobileSummaryForStep(step, m) {
      if (step === 1) {
        const setups = m.all.filter(item => item.role === 'kit');

        if (setups.length === 1) {
          const name = cleanSetupName(setups[0].title);
          return name.length > 30 ? '1 setup selected' : name;
        }

        if (setups.length > 1) {
          return `${setups.length} setups selected`;
        }

        return state.skippedSteps.has(1) ? 'Skipped' : 'Choose';
      }

      if (step === 2) {
        if (m.filterUnits > 0) {
          if (m.percent) {
            return `${m.filterUnits} filter${m.filterUnits === 1 ? '' : 's'} · ${m.percent}% off`;
          }

          return `${m.filterUnits} filter${m.filterUnits === 1 ? '' : 's'} selected`;
        }

        return state.skippedSteps.has(2) ? 'Skipped' : 'Optional';
      }

      const extraUnits = selectedUnitsByRole(m.all, 'accessory');

      if (extraUnits > 0) {
        return `${extraUnits} extra${extraUnits === 1 ? '' : 's'} selected`;
      }

      return state.mobileDone ? 'Done' : 'Optional';
    }

    function syncMobileWizard(m) {
      if (!els.stepPanels.length) return;

      const activeStep = state.mobileStep;

      root.dataset.mobileStep = String(activeStep);

      root.classList.toggle('is-mobile-finished', state.mobileDone);

      els.stepPanels.forEach(panel => {
        const step = Number(panel.dataset.fwubStepPanel || 0);
        const active =
          step === activeStep &&
          !(state.mobileDone && step === 3);

        panel.classList.toggle('is-mobile-active', active);
        panel.classList.toggle('is-mobile-collapsed', !active);
      });

      els.mobileSteps.forEach(button => {
        const step = Number(button.dataset.fwubMobileStepNav || 0);
        const active = step === activeStep;

        button.classList.toggle('is-active', active);

        if (active) {
          button.setAttribute('aria-current', 'step');
        } else {
          button.removeAttribute('aria-current');
        }

        const complete =
          (step === 1 &&
            (m.all.some(item => item.role === 'kit') ||
              state.skippedSteps.has(1))) ||
          (step === 2 &&
            (m.filterUnits > 0 ||
              state.skippedSteps.has(2) ||
              activeStep > 2)) ||
          (step === 3 &&
            (selectedUnitsByRole(m.all, 'accessory') > 0 ||
              state.mobileDone));

        button.classList.toggle('is-complete', complete);
      });

      els.stepNavSummaries.forEach(node => {
        const step = Number(node.dataset.fwubMobileStepSummary || 0);
        node.textContent = mobileSummaryForStep(step, m);
      });

      els.panelSummaries.forEach(node => {
        const step = Number(node.dataset.fwubPanelSummary || 0);
        node.textContent = mobileSummaryForStep(step, m);
      });
    }

    function scrollWizardIntoView() {
      if (!isMobileWizard()) return;

      const nav = root.querySelector('[data-fwub-mobile-steps]');
      if (!nav) return;

      const rect = nav.getBoundingClientRect();

      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        nav.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }

    function setMobileStep(step, options = {}) {
      const next = Math.max(1, Math.min(3, Number(step) || 1));

      if (next === 3 && options.keepDone !== true) {
        state.mobileDone = false;
      }

      state.mobileStep = next;

      const m = metrics();
      syncMobileWizard(m);

      if (options.scroll !== false) {
        window.setTimeout(scrollWizardIntoView, 40);
      }
    }

    function money(cents) {
      const value = (Number(cents) || 0) / 100;

      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency
        }).format(value);
      } catch (_) {
        return `${value.toFixed(2)} ${currency}`;
      }
    }

    function normalizeQuantity(value) {
      const number = Number(value);

      return Math.max(
        1,
        Math.min(99, Number.isFinite(number) ? Math.round(number) : 1)
      );
    }

    function normalizeAddonQuantity(value) {
      const number = Number(value);

      return Math.max(
        0,
        Math.min(99, Number.isFinite(number) ? Math.round(number) : 0)
      );
    }

    function parseIds(value) {
      return String(value || '')
        .split(',')
        .map(id => id.trim())
        .filter(Boolean);
    }

    function nativeQuantityInput() {
      const direct = mainForm?.querySelector('[name="quantity"]');

      if (direct && !root.contains(direct)) return direct;

      const candidates = [
        ...(productInfo?.querySelectorAll('[name="quantity"]') || [])
      ];

      return candidates.find(input => !root.contains(input)) || null;
    }

    function nativeQuantityShell() {
      const input = nativeQuantityInput();

      if (!input) return null;

      return (
        input.closest(
          '.product-form__quantity, .product-info-quantity-selector, [data-quantity-selector]'
        ) ||
        input.closest('quantity-input') ||
        null
      );
    }

    function nativeMainQuantity() {
      return normalizeQuantity(nativeQuantityInput()?.value || 1);
    }

    function mainQuantity() {
      if (state.mainSelected && state.mainBuilderQuantity > 0) {
        return normalizeQuantity(state.mainBuilderQuantity);
      }

      return nativeMainQuantity();
    }

    function syncNativeMainQuantity(quantity) {
      const input = nativeQuantityInput();

      if (!input) return;

      const next = normalizeQuantity(quantity);

      if (Number(input.value) === next) return;

      input.value = String(next);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function mainVariantId() {
      return String(mainForm?.querySelector('[name="id"]')?.value || '');
    }

    function isMainCard(card) {
      return card?.dataset.mainCard === 'true';
    }

    function selectedOption(card) {
      const select = card?.querySelector('[data-fwub-variant]');

      if (!select) return null;

      if (isMainCard(card)) {
        const nativeId = mainVariantId();

        if (nativeId) {
          const matching = [...select.options].find(
            option => String(option.value) === nativeId
          );

          if (matching) return matching;
        }
      }

      return select.selectedOptions?.[0] || select.options?.[0] || null;
    }

    function currentVariantKey(card) {
      const option = selectedOption(card);

      return option ? String(option.value) : '';
    }

    function readCard(card, quantity = 1) {
      const option = selectedOption(card);
      const button = card?.querySelector('[data-fwub-toggle]');

      if (!option || !button) return null;

      return {
        productId: String(card.dataset.productId || ''),
        id: Number(option.value),
        role: card.dataset.role || 'accessory',
        title: button.dataset.productTitle || 'Product',
        variantTitle: option.dataset.title || option.textContent.trim(),
        url: button.dataset.productUrl || '#',
        image: button.dataset.productImage || '',
        price: Number(option.dataset.price || 0),
        compareAt: Number(option.dataset.compare || 0),
        available: option.dataset.available === 'true',
        orderStatus:
          option.dataset.orderStatus ||
          (option.dataset.available === 'true' ? 'normal' : 'soldout'),
        quantity: normalizeQuantity(quantity),
        main: isMainCard(card),
        includedVariantIds: parseIds(card.dataset.includedVariantIds)
      };
    }

    function selectedItemsForCard(card) {
      if (isMainCard(card)) {
        if (!state.mainSelected || state.mainBuilderQuantity <= 0) return [];

        const item = readCard(card, state.mainBuilderQuantity);

        return item ? [item] : [];
      }

      const productId = String(card?.dataset.productId || '');

      return [...state.selected.values()].filter(
        item => item.productId === productId
      );
    }

    function cardQuantity(card) {
      if (isMainCard(card)) {
        return state.mainSelected
          ? normalizeAddonQuantity(state.mainBuilderQuantity)
          : 0;
      }

      const key = currentVariantKey(card);

      if (!key) return 0;

      const item = state.selected.get(key);

      return item ? normalizeAddonQuantity(item.quantity) : 0;
    }

    function setCardQuantity(card, next) {
      if (!card) return;

      if (isMainCard(card)) {
        const quantity = normalizeAddonQuantity(next);

        if (quantity <= 0) {
          state.mainSelected = false;
          state.mainBuilderQuantity = 0;
          render();
          return;
        }

        const item = readCard(card, quantity);

        if (!item || !item.available) return;

        state.mainSelected = true;
        state.mainBuilderQuantity = quantity;

        syncNativeMainQuantity(quantity);
        render();

        return;
      }

      const option = selectedOption(card);

      if (!option) return;

      const key = String(option.value);
      const quantity = normalizeAddonQuantity(next);

      if (quantity <= 0) {
        state.selected.delete(key);
      } else {
        const item = readCard(card, quantity);

        if (!item || !item.available) return;

        state.selected.set(key, item);
      }

      render();
    }

    function items() {
      const output = [];

      if (
        mainCard &&
        state.mainSelected &&
        state.mainBuilderQuantity > 0
      ) {
        const mainItem = readCard(mainCard, state.mainBuilderQuantity);

        if (mainItem) output.push(mainItem);
      }

      state.selected.forEach(item => output.push(item));

      return output;
    }

    function selectedKitItems(allItems) {
      return allItems.filter(item => item.role === 'kit');
    }

    function includedBy(item, allItems) {
      if (!item) return [];

      const variantId = String(item.id);

      return selectedKitItems(allItems)
        .filter(
          kit =>
            String(kit.id) !== variantId &&
            kit.includedVariantIds.includes(variantId)
        )
        .map(kit => conciseKitName(kit.title));
    }

    function highestKitName(names) {
      const clean = [...new Set((names || []).filter(Boolean))];

      const mega = clean.find(name => /Mega Pro Kit/i.test(name));
      if (mega) return mega;

      const pro = clean.find(name => /Pro Kit/i.test(name));
      if (pro) return pro;

      const basic = clean.find(name => /Basic Kit|V2 VND\/CPL/i.test(name));
      if (basic) return basic;

      return clean[0] || '';
    }

    function metrics() {
      const all = items();
      const kitItems = all.filter(item => item.role === 'kit');
      const filterItems = all.filter(item => item.role === 'filter');

      const filterUnits = filterItems.reduce(
        (sum, item) => sum + normalizeQuantity(item.quantity),
        0
      );

      const hasKit = kitItems.length > 0;

      const percent = filterUnits >= 2 ? 15 : (hasKit && filterUnits === 1 ? 12 : 0);

      const subtotal = all.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );

      const filterSubtotal = filterItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );

      const savings = percent
        ? Math.round((filterSubtotal * percent) / 100)
        : 0;

      const kitSavings = kitItems.reduce(
        (sum, item) =>
          sum + Math.max(0, item.compareAt - item.price) * item.quantity,
        0
      );

      const selectedUnits = all.reduce(
        (sum, item) => sum + normalizeQuantity(item.quantity),
        0
      );

      return {
        all,
        hasKit,
        filterUnits,
        selectedUnits,
        percent,
        subtotal,
        savings,
        kitSavings,
        total: Math.max(0, subtotal - savings)
      };
    }

    function meterCopy(m) {
      const count = m.filterUnits ? `${m.filterUnits} filter${m.filterUnits === 1 ? '' : 's'} selected` : 'No filters selected';
      if (m.filterUnits >= 2) return {
        stage: 'max', value: '15% OFF', title: 'Maximum filter savings unlocked', count,
        next: `You save ${money(m.savings)} on filters.`
      };
      if (m.percent === 12) return {
        stage: 'unlocked', value: '12% OFF', title: 'Filter savings unlocked', count,
        next: `Save ${money(m.savings)} · Add 1 more for 15%.`
      };
      return {
        stage: m.filterUnits ? 'ready' : 'locked', value: '0% UNLOCKED',
        title: 'Choose 2 filters for 15% off', count,
        next: m.filterUnits === 1 ? 'Add 1 more filter for 15% off.' : 'Add 2 filters for 15% off. No kit needed.'
      };
    }

    function orderStatusText(item) {
      if (!item) return '';
      if (item.orderStatus === 'preorder') return 'Pre-order';
      if (item.orderStatus === 'backorder') return 'Backorder';

      return '';
    }

    function renderSelectedSummary(card, summary, selectedVariants, allItems) {
      if (!summary) return;

      summary.replaceChildren();

      const hasVariantChoices = card.classList.contains('has-variants');

      if (
        isMainCard(card) ||
        !hasVariantChoices ||
        !selectedVariants.length
      ) {
        summary.hidden = true;
        return;
      }

      summary.hidden = false;

      selectedVariants.forEach(selectedItem => {
        const row = document.createElement('div');
        row.className = 'fwub-card__selected-row';

        const copyWrap = document.createElement('span');
        copyWrap.className = 'fwub-card__selected-copy';

        const variantName =
          selectedItem.variantTitle &&
          selectedItem.variantTitle !== 'Default Title'
            ? selectedItem.variantTitle
            : selectedItem.title;

        const copy = document.createElement('strong');
        copy.textContent = `${variantName} ×${selectedItem.quantity}`;

        copyWrap.append(copy);

        const kitNames = includedBy(selectedItem, allItems);

        if (kitNames.length) {
          const warning = document.createElement('small');
          warning.textContent = 'Extra';
          copyWrap.append(warning);
        }

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'fwub-card__selected-remove';
        remove.dataset.fwubRemoveVariant = String(selectedItem.id);
        remove.setAttribute('aria-label', `Remove ${variantName}`);
        remove.textContent = '×';

        row.append(copyWrap, remove);
        summary.append(row);
      });
    }

    function updateCard(card, allItems) {
      const isMain = isMainCard(card);

      const item = readCard(
        card,
        isMain ? mainQuantity() : 1
      );

      const button = card.querySelector('[data-fwub-toggle]');
      const stateLabel = card.querySelector('[data-fwub-state]');
      const price = card.querySelector('[data-fwub-card-price]');
      const quantityOutput = card.querySelector('[data-fwub-item-qty-value]');
      const quantityMinus = card.querySelector('[data-fwub-item-qty-minus]');
      const quantityPlus = card.querySelector('[data-fwub-item-qty-plus]');
      const summary = card.querySelector('[data-fwub-selected-variants]');
      const includedNote = card.querySelector('[data-fwub-included-note]');

      const currentQuantity = cardQuantity(card);
      const selectedVariants = selectedItemsForCard(card);
      const hasAnySelected = selectedVariants.length > 0;

      const currentSelected = isMain
        ? state.mainSelected
        : currentQuantity > 0;

      const kitNames = item ? includedBy(item, allItems) : [];

      card.classList.toggle('is-selected', hasAnySelected);
      card.classList.toggle('is-current-option-selected', currentSelected);
      card.classList.toggle('is-already-included', kitNames.length > 0);

      if (item) {
        card.dataset.orderStatus = item.orderStatus || 'normal';
        card.classList.toggle('is-soldout', !item.available);
      }

      if (button) {
        button.disabled = !item?.available;
        button.setAttribute(
          'aria-pressed',
          currentSelected ? 'true' : 'false'
        );
      }

      if (price && item) {
        price.textContent = money(item.price);
        const amount = Number(selectedOption(card)?.dataset.compare || 0);
        let compare = card.querySelector('.fwub-card__compare-price');
        if (amount > item.price) {
          if (!compare) {compare = document.createElement('s'); compare.className = 'fwub-card__compare-price'; price.before(compare);}
          compare.hidden = false; compare.textContent = money(amount);
        } else if (compare) compare.hidden = true;
      }

      if (quantityOutput) {
        quantityOutput.textContent = String(currentQuantity);
      }

      if (quantityMinus) {
        quantityMinus.disabled = currentQuantity <= 0;
      }

      if (quantityPlus) {
        quantityPlus.disabled =
          !item?.available ||
          currentQuantity >= 99;
      }

      renderSelectedSummary(
        card,
        summary,
        selectedVariants,
        allItems
      );

      if (includedNote) {
        const kitOverlapName =
          item?.role === 'kit'
            ? highestKitName(kitNames)
            : '';

        if (item?.role === 'kit' && kitOverlapName) {
          includedNote.hidden = false;
          includedNote.textContent = `Included in ${kitOverlapName}.`;
        } else if (item && kitNames.length && !currentSelected) {
          includedNote.hidden = false;
          includedNote.textContent = `Included in ${joinNames(kitNames)}.`;
        } else {
          includedNote.hidden = true;
          includedNote.textContent = '';
        }
      }

      if (!stateLabel) return;

      if (!item?.available) {
        stateLabel.textContent = 'Sold out';
        return;
      }

      const orderText = orderStatusText(item);

      if (currentSelected) {
        stateLabel.textContent = orderText || '';
        return;
      }

      if (kitNames.length) {
        stateLabel.textContent = 'Included';
        return;
      }

      if (orderText) {
        stateLabel.textContent = orderText;
        return;
      }

      stateLabel.textContent = '';
    }

    function dynamicCheckoutElements() {
      return [
        ...new Set([
          ...(productInfo?.querySelectorAll('.shopify-payment-button') || []),
          ...(productInfo?.querySelectorAll('[data-shopify="payment-button"]') || [])
        ])
      ];
    }

    function syncDynamicCheckout() {
      const shouldHide = state.selected.size > 0;

      dynamicCheckoutElements().forEach(element => {
        element.classList.toggle(
          'fwub-dynamic-checkout-hidden',
          shouldHide
        );
      });
    }

    function syncNativeQuantityVisibility() {
      const shell = nativeQuantityShell();

      if (!shell) return;

      const shouldHide =
        state.selected.size > 0 &&
        !state.mainSelected;

      shell.classList.toggle(
        'fwub-native-quantity-hidden',
        shouldHide
      );
    }

    function render() {
      const m = metrics();
      const meter = meterCopy(m);
      const progress = Math.min(2, m.filterUnits);
      const fill = `${progress * 50}%`;

      els.cards.forEach(card => updateCard(card, m.all));

      if (els.summary) {
        els.summary.hidden = !m.all.length;
      }

      if (els.meter) {
        els.meter.dataset.stage = meter.stage;
      }

      if (els.meterTitle) {
        els.meterTitle.textContent = meter.title;
      }

      if (els.meterValue) {
        els.meterValue.textContent = meter.value;
      }

      if (els.meterCount) {
        els.meterCount.textContent = meter.count;
      }

      if (els.meterNext) {
        els.meterNext.textContent = meter.next;
      }

      if (els.meterFill) {
        els.meterFill.style.width = fill;
      }

      if (els.meterTrack) {
        els.meterTrack.setAttribute(
          'aria-valuenow',
          String(progress)
        );

        els.meterTrack.setAttribute(
          'aria-valuetext',
          meter.value
        );
      }

      els.meterNodes.forEach((node, index) => {
        node.classList.toggle(
          'is-reached',
          progress >= index + 1
        );
      });

      if (els.subtotal) {
        els.subtotal.textContent = money(m.subtotal);
      }

      if (els.savingsRow) {
        els.savingsRow.hidden = !m.percent;
      }

      if (els.savingsLabel && m.percent) {
        els.savingsLabel.textContent =
          `${m.percent}% filter savings`;
      }

      if (els.savings) {
        els.savings.textContent = `−${money(m.savings)}`;
      }

      if (els.kitSavingsRow) {
        els.kitSavingsRow.hidden = !m.kitSavings;
      }

      if (els.kitSavings) {
        els.kitSavings.textContent = `−${money(m.kitSavings)}`;
      }

      if (els.total) {
        els.total.textContent = money(m.total);
      }

      syncMobileWizard(m);
      syncDynamicCheckout();
      syncNativeQuantityVisibility();
    }

    function setStatus(message, error = false) {
      if (!els.status) return;

      els.status.textContent = message || '';

      els.status.classList.toggle(
        'is-error',
        Boolean(error)
      );
    }

    function toggleCard(card) {
      if (!card) return;

      const item = readCard(
        card,
        isMainCard(card) ? mainQuantity() : 1
      );

      if (!item || !item.available || !item.id) return;

      if (isMainCard(card)) {
        if (state.mainSelected) {
          setCardQuantity(card, 0);
        } else {
          setCardQuantity(card, nativeMainQuantity());
        }

        return;
      }

      const quantity = cardQuantity(card);

      setCardQuantity(
        card,
        quantity > 0 ? 0 : 1
      );
    }

    function changeVariant(card) {
      if (!card) return;

      render();
    }

    function payload(m) {
      return m.all.map(item => ({
        id: item.id,
        quantity: item.quantity,
        properties: {
          '_FREEWELL.BUILDER': profile,
          '_FW Role': item.role,
          '_FW Tier': m.percent
            ? `${m.percent}%`
            : 'None'
        }
      }));
    }

    async function addSetup() {
      if (state.busy) return;

      const m = metrics();

      if (!m.all.length) {
        setStatus(
          'Choose at least one product first.',
          true
        );
        return;
      }

      const unavailable = m.all.find(
        item =>
          !item.available ||
          !Number.isInteger(item.id)
      );

      if (unavailable) {
        setStatus(
          `${unavailable.title} is not available in the selected option.`,
          true
        );
        return;
      }

      state.busy = true;

      setStatus('Adding your selections…');

      try {
        const response = await fetch(
          `${routesRoot}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
              items: payload(m)
            })
          }
        );

        let data = {};

        try {
          data = await response.json();
        } catch (_) {
          data = {};
        }

        if (!response.ok) {
          throw new Error(
            data.description ||
            data.message ||
            'Shopify could not add these products.'
          );
        }

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {
              source: 'freewell-builder',
              response: data
            }
          })
        );

        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            detail: {
              source: 'freewell-builder'
            }
          })
        );

        const code =
          discountMode === 'code' && m.percent
            ? codes[m.percent]
            : '';

        const target =
          addDestination === 'stay'
            ? ''
            : '/cart';

        if (code) {
          const redirect =
            target ||
            window.location.pathname +
            window.location.search;

          window.location.assign(
            `${routesRoot}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`
          );

          return;
        }

        if (target) {
          window.location.assign(
            `${routesRoot}${target.replace(/^\//, '')}`
          );

          return;
        }

        state.busy = false;

        setStatus('Added to cart.');

        render();
      } catch (error) {
        state.busy = false;

        setStatus(
          error?.message ||
          'Could not add these products. Please try again.',
          true
        );

        render();
      }
    }

    root.addEventListener('click', event => {
      const mobileStepNav = event.target.closest(
        '[data-fwub-mobile-step-nav]'
      );

      if (mobileStepNav && isMobileWizard()) {
        event.preventDefault();

        setMobileStep(
          Number(
            mobileStepNav.dataset.fwubMobileStepNav ||
            1
          )
        );

        return;
      }

      const mobileAction = event.target.closest(
        '[data-fwub-mobile-action]'
      );

      if (mobileAction && isMobileWizard()) {
        event.preventDefault();

        const action =
          mobileAction.dataset.fwubMobileAction ||
          '';

        if (action === 'skip-1') {
          state.skippedSteps.add(1);
          setMobileStep(2);
          return;
        }

        if (action === 'skip-2') {
          state.skippedSteps.add(2);
          setMobileStep(3);
          return;
        }

        if (action === 'continue-2') {
          state.skippedSteps.delete(2);
          setMobileStep(3);
          return;
        }

        if (action === 'done-3') {
          state.mobileDone = true;
          render();

          const summary = root.querySelector(
            '[data-fwub-summary]'
          );

          if (summary && !summary.hidden) {
            window.setTimeout(() => {
              summary.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
              });
            }, 40);
          }

          return;
        }
      }

      if (
        event.target.closest(
          '[data-fwub-product-link]'
        )
      ) {
        return;
      }

      const removeVariant = event.target.closest(
        '[data-fwub-remove-variant]'
      );

      if (removeVariant) {
        event.preventDefault();
        event.stopPropagation();

        const key = String(
          removeVariant.dataset.fwubRemoveVariant ||
          ''
        );

        if (key) {
          state.selected.delete(key);
        }

        render();

        return;
      }

      const minus = event.target.closest(
        '[data-fwub-item-qty-minus]'
      );

      if (minus) {
        event.preventDefault();
        event.stopPropagation();

        const card = minus.closest(
          '[data-fwub-card]'
        );

        setCardQuantity(
          card,
          cardQuantity(card) - 1
        );

        return;
      }

      const plus = event.target.closest(
        '[data-fwub-item-qty-plus]'
      );

      if (plus) {
        event.preventDefault();
        event.stopPropagation();

        const card = plus.closest(
          '[data-fwub-card]'
        );

        setCardQuantity(
          card,
          cardQuantity(card) + 1
        );

        return;
      }

      const toggle = event.target.closest(
        '[data-fwub-toggle]'
      );

      if (toggle) {
        const card = toggle.closest(
          '[data-fwub-card]'
        );

        toggleCard(card);

        if (
          isMobileWizard() &&
          card?.dataset.role === 'kit' &&
          cardQuantity(card) > 0
        ) {
          state.skippedSteps.delete(1);

          window.setTimeout(
            () => setMobileStep(2),
            120
          );
        }
      }
    });

    root.addEventListener('change', event => {
      if (
        event.target.matches(
          '[data-fwub-variant]'
        )
      ) {
        changeVariant(
          event.target.closest(
            '[data-fwub-card]'
          )
        );
      }
    });

    if (mainForm) {
      mainForm.addEventListener(
        'click',
        event => {
          const addButton =
            event.target.closest?.(
              NATIVE_ADD_SELECTOR
            );

          if (
            !addButton ||
            state.selected.size === 0
          ) {
            return;
          }

          event.preventDefault();
          event.stopImmediatePropagation();

          addSetup();
        },
        true
      );

      mainForm.addEventListener(
        'submit',
        event => {
          if (state.selected.size === 0) {
            return;
          }

          event.preventDefault();
          event.stopImmediatePropagation();

          addSetup();
        },
        true
      );
    }

    const scheduleMainSync = () =>
      window.setTimeout(() => {
        if (state.mainSelected) {
          state.mainBuilderQuantity =
            nativeMainQuantity();
        }

        render();
      }, 30);

    productInfo?.addEventListener(
      'change',
      event => {
        if (root.contains(event.target)) {
          return;
        }

        scheduleMainSync();
      }
    );

    productInfo?.addEventListener(
      'input',
      event => {
        if (root.contains(event.target)) {
          return;
        }

        scheduleMainSync();
      }
    );

    const handleViewportChange = () => {
      if (!isMobileWizard()) {
        els.stepPanels.forEach(panel => {
          panel.classList.remove(
            'is-mobile-collapsed'
          );
        });

        return;
      }

      render();
    };

    if (
      typeof mobileQuery.addEventListener ===
      'function'
    ) {
      mobileQuery.addEventListener(
        'change',
        handleViewportChange
      );
    } else if (
      typeof mobileQuery.addListener ===
      'function'
    ) {
      mobileQuery.addListener(
        handleViewportChange
      );
    }

    state.mainSelected = false;
    state.mainBuilderQuantity = 0;

    render();
  }

  function initAll(scope = document) {
    scope
      .querySelectorAll?.(ROOT_SELECTOR)
      .forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll(),
      {
        once: true
      }
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    event => {
      initAll(event.target);
    }
  );
})();
